import { useMemo } from "react";
import { extent, index, rgb, scaleLinear } from "d3";
import type { ScaleLinear } from "d3";
import { getClassificationServicesCategory, ProductClass } from "@atlas-int/data";

const hs22ServicesCategory = getClassificationServicesCategory(ProductClass.HS22Products);
import { OpportunitySizeOption } from "./utils";
import type {
  GrowthCPY,
  GrowthPY,
  MetaDataLookup,
  ProductYearLookup,
  ScatterplotDatum,
  FilteredDataLookup,
} from "./scatterplotTypes";

interface UseScatterplotScalesParams {
  countryProductYear: GrowthCPY[];
  productYear: GrowthPY[];
  width: number;
  height: number;
  yPadding: number;
  hideExports: string;
  ySelection: string;
  totalExportLookup: ProductYearLookup;
  metaDataLookup: MetaDataLookup;
  sizing: string;
  hiddenCategories: string[];
  colorMap: Map<string, string>;
  productClass: string;
}

interface UseScatterplotScalesResult {
  filteredData: ScatterplotDatum[];
  filteredDataLookup: FilteredDataLookup;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  rScale: ScaleLinear<number, number>;
}

export function useScatterplotScales({
  countryProductYear,
  productYear,
  width,
  height,
  yPadding,
  hideExports,
  ySelection,
  totalExportLookup,
  metaDataLookup,
  sizing,
  hiddenCategories,
  colorMap,
  productClass,
}: UseScatterplotScalesParams): UseScatterplotScalesResult {
  return useMemo(() => {
    const filteredCPY = countryProductYear.filter((d) => {
      const productData = metaDataLookup.get(d.productId as string)?.data;
      const topParentId = productData?.topParent?.productId;

      // Exclude services (sector 14) for HS22 - services shouldn't be in growth potential plot
      if (productClass === "HS22" && topParentId === hs22ServicesCategory) {
        return false;
      }

      const showFeasibility =
        productClass === "HS22" ? true : productData?.showFeasibility;
      const hasValidDistance = d.distance != null && Number(d.distance) > 0;
      return (
        showFeasibility &&
        hasValidDistance &&
        (hideExports === "On" ? Number(d.exportRca) < 1 : true)
      );
    });

    const xScale = scaleLinear()
      .domain(
        extent(filteredCPY, ({ distance }) =>
          parseFloat(String(distance)),
        ) as [number, number],
      )
      .range([yPadding, width + yPadding]);

    const yScale = scaleLinear()
      .domain(
        extent(filteredCPY, (d) =>
          ySelection === "cog"
            ? parseFloat(String(d.cog))
            : parseFloat(
                String(
                  totalExportLookup.get(d.productId as string)?.pci,
                ),
              ),
        ) as [number, number],
      )
      .range([height - yPadding / 2, 0]);
    const fdLookup = index(filteredCPY, (d) => d.productId as string);

    const rScale = scaleLinear()
      .domain(
        extent(
          productYear.filter(
            (d) => !!fdLookup.get(d.productId as string),
          ),
          (d) => parseFloat(String(d.exportValue)),
        ) as [number, number],
      )
      .range([3, 50]);
    const filtered: ScatterplotDatum[] = filteredCPY
      .map((d) => {
        const productData = metaDataLookup.get(
          d.productId as string,
        )?.data;
        const topParentId = productData?.topParent?.productId ?? "";
        const pyRow = totalExportLookup.get(d.productId as string);
        return {
          cx: xScale(Number(d.distance)),
          cy: yScale(
            ySelection === "cog"
              ? Number(d.cog)
              : Number(pyRow?.pci),
          ),
          r:
            sizing === OpportunitySizeOption.WorldTrade
              ? rScale(Number(pyRow?.exportValue))
              : 4,
          opacity: hiddenCategories.includes(topParentId) ? 0.25 : 1,
          fillOpacity: Number(d.exportRca) < 1 ? 0.65 : 0.95,
          fill: hiddenCategories.includes(topParentId)
            ? "white"
            : Number(d.exportRca) < 1
              ? (colorMap?.get(topParentId) ?? "")
              : rgb(colorMap?.get(topParentId) ?? "")
                  .darker()
                  .formatHex(),
          topParentId: topParentId,
          showFeasibility: productData?.showFeasibility,
          ...d,
        } as ScatterplotDatum;
      })
      .sort((d1, d2) => d2.r - d1.r);
    const filteredDataLookup: FilteredDataLookup = index(
      filtered,
      (d) => d.productId as string,
    ) as FilteredDataLookup;

    return {
      filteredData: filtered,
      filteredDataLookup,
      xScale,
      yScale,
      rScale,
    };
  }, [
    countryProductYear,
    width,
    height,
    productYear,
    hideExports,
    ySelection,
    totalExportLookup,
    metaDataLookup,
    sizing,
    hiddenCategories,
    colorMap,
    productClass,
  ]);
}
